import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Empty, Row, Select, Space, Table, Typography } from "antd";
import {
    BankOutlined, FileExcelOutlined, FilePdfOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../../variables/api/axiosInstance";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import { displayNumbers } from "../../variables/numbersFunctions";

const { Title, Text } = Typography;
const PRIMARY = "#242166";

const fmt = (v) => `${displayNumbers(Math.trunc(Number(v) || 0))}`;
const isPoolRow = (nom) => (nom || "").toUpperCase().includes("POOL");

/**
 * État « Sinistres payés par compagnie » (croisé compagnie × branche GUM/VT/TPV/MOTO), par exercice.
 * Montant = règlements de sinistres payés (décaissements, hors recours), par date de règlement ;
 * le Pool règle GUM/VT, les compagnies règlent TPV/MOTO.
 * Source : GET /api/sinistre/etats/payes-par-compagnie?startDate&endDate.
 */
const SinistresPayesParCompagnie = () => {
    const CURRENT_YEAR = 2026; // année de référence par défaut (évite Date.now non déterministe)
    const [year, setYear] = useState(2025);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const years = useMemo(() => {
        const arr = [];
        for (let y = CURRENT_YEAR; y >= 2015; y--) arr.push({ value: y, label: `${y}` });
        return arr;
    }, []);

    const fetchData = (y) => {
        setLoading(true);
        axiosInstance
            .get("/api/sinistre/etats/payes-par-compagnie", {
                params: { startDate: `${y}-01-01`, endDate: `${y}-12-31` },
            })
            .then((res) => setData(res?.data || []))
            .catch(() => toast.error("Erreur lors du chargement de l'état"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData(year);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pool en tête, puis compagnies par total décroissant.
    const rows = useMemo(() => {
        const copy = [...(data || [])];
        copy.sort((a, b) => {
            const pa = isPoolRow(a?.compagnie) ? 1 : 0;
            const pb = isPoolRow(b?.compagnie) ? 1 : 0;
            if (pa !== pb) return pb - pa; // Pool d'abord
            return (Number(b?.total) || 0) - (Number(a?.total) || 0);
        });
        return copy;
    }, [data]);

    const totals = useMemo(
        () =>
            (data || []).reduce(
                (acc, r) => {
                    acc.gum += Number(r?.gum) || 0;
                    acc.vt += Number(r?.vt) || 0;
                    acc.tpv += Number(r?.tpv) || 0;
                    acc.moto += Number(r?.moto) || 0;
                    acc.total += Number(r?.total) || 0;
                    return acc;
                },
                { gum: 0, vt: 0, tpv: 0, moto: 0, total: 0 }
            ),
        [data]
    );

    const columns = [
        {
            title: "Compagnie",
            key: "compagnie",
            render: (_, r) => (
                <Text strong={isPoolRow(r?.compagnie)}>{r?.compagnie || "-"}</Text>
            ),
        },
        { title: "GUM", key: "gum", align: "right", render: (_, r) => fmt(r?.gum) },
        { title: "VT", key: "vt", align: "right", render: (_, r) => fmt(r?.vt) },
        { title: "TPV", key: "tpv", align: "right", render: (_, r) => fmt(r?.tpv) },
        { title: "MOTO", key: "moto", align: "right", render: (_, r) => fmt(r?.moto) },
        {
            title: "TOTAL",
            key: "total",
            align: "right",
            render: (_, r) => <Text strong>{fmt(r?.total)}</Text>,
        },
    ];

    const headers = ["Compagnie", "GUM", "VT", "TPV", "MOTO", "TOTAL"];
    const exportRows = () =>
        rows.map((r) => [r?.compagnie || "", fmt(r?.gum), fmt(r?.vt), fmt(r?.tpv), fmt(r?.moto), fmt(r?.total)]);
    const totalRow = ["TOTAL", fmt(totals.gum), fmt(totals.vt), fmt(totals.tpv), fmt(totals.moto), fmt(totals.total)];

    const handleExcel = () => {
        if (!rows.length) return toast.warn("Aucune donnée à exporter");
        const aoa = [
            [`SINISTRES PAYÉS PAR COMPAGNIE — EXERCICE ${year}`],
            ["GUM/VT = décaissé par le Pool ; TPV/MOTO = sinistres déclarés des compagnies"],
            [],
            headers,
            ...exportRows(),
            totalRow,
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Payés par compagnie");
        XLSX.writeFile(wb, `Sinistres_payes_par_compagnie_${year}.xlsx`);
    };

    const handlePdf = () => {
        if (!rows.length) return toast.warn("Aucune donnée à exporter");
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text(`SINISTRES PAYÉS PAR COMPAGNIE AU 31 DÉCEMBRE ${year}`, 14, 15);
        autoTable(doc, {
            startY: 22,
            head: [headers],
            body: exportRows(),
            foot: [totalRow],
            styles: { fontSize: 9 },
            headStyles: { fillColor: [36, 33, 102] },
            footStyles: { fillColor: [230, 230, 245], textColor: 20, fontStyle: "bold" },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
        });
        doc.save(`Sinistres_payes_par_compagnie_${year}.pdf`);
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <Card style={{ marginBottom: 16, background: PRIMARY, borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
                                <Space align="center">
                                    <BankOutlined style={{ fontSize: 26, color: "#fff" }} />
                                    <div>
                                        <Title level={4} style={{ color: "#fff", margin: 0 }}>
                                            Sinistres payés par compagnie
                                        </Title>
                                        <Text style={{ color: "#e5e7eb" }}>
                                            GUM/VT réglés par le Pool · TPV/MOTO réglés par les compagnies · par date de règlement
                                        </Text>
                                    </div>
                                </Space>
                            </Card>

                            <Card style={{ marginBottom: 16, borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="bottom">
                                    <Col xs={24} md={8}>
                                        <Text type="secondary">Exercice</Text>
                                        <Select
                                            style={{ width: "100%" }}
                                            options={years}
                                            value={year}
                                            onChange={(y) => {
                                                setYear(y);
                                                fetchData(y);
                                            }}
                                        />
                                    </Col>
                                    <Col xs={24} md={16}>
                                        <Space>
                                            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchData(year)}>
                                                Actualiser
                                            </Button>
                                            <Button icon={<FileExcelOutlined />} onClick={handleExcel} disabled={!rows.length}>
                                                Excel
                                            </Button>
                                            <Button icon={<FilePdfOutlined />} onClick={handlePdf} disabled={!rows.length}>
                                                PDF
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 12 }}>
                                <Table
                                    size="small"
                                    rowKey={(r) => r?.compagnie}
                                    columns={columns}
                                    dataSource={rows}
                                    loading={loading}
                                    scroll={{ x: "max-content" }}
                                    pagination={false}
                                    locale={{ emptyText: <Empty description="Aucune donnée pour cet exercice" /> }}
                                    summary={() =>
                                        rows.length ? (
                                            <Table.Summary fixed>
                                                <Table.Summary.Row style={{ background: "#fafafa" }}>
                                                    <Table.Summary.Cell index={0}>
                                                        <Text strong>TOTAL</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} align="right"><Text strong>{fmt(totals.gum)}</Text></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={2} align="right"><Text strong>{fmt(totals.vt)}</Text></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={3} align="right"><Text strong>{fmt(totals.tpv)}</Text></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={4} align="right"><Text strong>{fmt(totals.moto)}</Text></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={5} align="right"><Text strong style={{ color: PRIMARY }}>{fmt(totals.total)}</Text></Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        ) : null
                                    }
                                />
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SinistresPayesParCompagnie;
